/**
 * Type-safe error handling utilities for API errors.
 * Provides proper typing for Axios errors and API responses.
 */

import { AxiosError } from 'axios';
import { formatValidationErrors, ApiValidationError } from '../constants/messages';

/**
 * Structure of an Axios error response with API detail
 */
export interface ApiErrorResponse {
  // Sur un 422 FastAPI, `detail` est un array d'erreurs de validation Pydantic.
  detail?: string | ApiValidationError[] | {
    message?: string;
    error?: string;
    quota_exceeded?: boolean;
    quota_details?: QuotaDetails;
  };
}

/**
 * Quota error details from API
 */
export interface QuotaDetails {
  requested: {
    cpu?: number;
    ram?: number;
    storage?: number;
  };
  available: {
    cpu?: number;
    ram?: number;
    storage?: number;
  };
  total: {
    cpu?: number;
    ram?: number;
    storage?: number;
  };
}

/**
 * Type guard to check if an error is an AxiosError
 */
export function isAxiosError(error: unknown): error is AxiosError<ApiErrorResponse> {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
}

/**
 * Extract error message from various error types
 * Handles Axios errors, standard Error objects, and unknown errors
 *
 * @param error - The error to extract message from
 * @param defaultMessage - Default message if extraction fails
 * @returns User-friendly error message
 */
export function getErrorMessage(error: unknown, defaultMessage = 'Une erreur est survenue'): string {
  // Handle Axios errors
  if (isAxiosError(error)) {
    const detail = error.response?.data?.detail;

    if (typeof detail === 'string') {
      return detail;
    }

    // FastAPI Pydantic 422 : `detail` est un array d'objets {type, loc, msg, ...}.
    // Sans cette branche, le caller faisait `toast.error(detail)` → React #31
    // (Objects are not valid as a React child).
    // Parseur unique partage avec getApiErrorMessage — ne pas reimplementer ici,
    // les deux versions avaient diverge (celle-ci perdait le nom du champ).
    if (Array.isArray(detail)) {
      const validationMessage = formatValidationErrors(detail);
      if (validationMessage) return validationMessage;
    }

    if (typeof detail === 'object' && detail !== null && !Array.isArray(detail)) {
      if (detail.message) return detail.message;
      if (detail.error) return detail.error;
    }

    // Fallback to error message
    if (error.message) {
      return error.message;
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Handle objects with response property (non-Axios HTTP errors)
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as Record<string, unknown>;

    if ('response' in errorObj && typeof errorObj.response === 'object' && errorObj.response !== null) {
      const response = errorObj.response as Record<string, unknown>;

      if ('data' in response && typeof response.data === 'object' && response.data !== null) {
        const data = response.data as Record<string, unknown>;

        if ('detail' in data) {
          if (typeof data.detail === 'string') {
            return data.detail;
          }
          if (typeof data.detail === 'object' && data.detail !== null) {
            const detail = data.detail as Record<string, unknown>;
            if ('message' in detail && typeof detail.message === 'string') {
              return detail.message;
            }
          }
        }
      }
    }

    // Check for message property directly on the object
    if ('message' in errorObj && typeof errorObj.message === 'string') {
      return errorObj.message;
    }
  }

  return defaultMessage;
}

/**
 * Check if error is a quota-related error
 */
export function isQuotaError(error: unknown): boolean {
  if (isAxiosError(error)) {
    const detail = error.response?.data?.detail;

    if (typeof detail === 'string') {
      return detail.toLowerCase().includes('quota');
    }

    if (typeof detail === 'object' && detail !== null && !Array.isArray(detail) && detail.quota_exceeded) {
      return true;
    }
  }

  return false;
}

/**
 * Extract quota details from error if available
 */
export function getQuotaDetails(error: unknown): QuotaDetails | null {
  if (isAxiosError(error)) {
    const detail = error.response?.data?.detail;

    if (typeof detail === 'object' && detail !== null && !Array.isArray(detail) && detail.quota_details) {
      return detail.quota_details;
    }
  }

  return null;
}

/**
 * Check if error is a network/connection error
 */
export function isNetworkError(error: unknown): boolean {
  if (isAxiosError(error)) {
    // No response means network issue
    if (!error.response) {
      const code = error.code;
      if (code === 'ERR_NETWORK' || code === 'ECONNREFUSED' || code === 'ENOTFOUND') {
        return true;
      }
      // Check error message for network-related keywords
      const message = error.message?.toLowerCase() || '';
      if (message.includes('network') || message.includes('connection')) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Check if error is a timeout error
 */
export function isTimeoutError(error: unknown): boolean {
  if (isAxiosError(error)) {
    // Axios timeout errors
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return true;
    }

    // Check message for timeout keywords
    const message = error.message?.toLowerCase() || '';
    if (message.includes('timeout') || message.includes('timed out')) {
      return true;
    }
  }

  return false;
}

/**
 * Check if error is an authentication error (401)
 */
export function isAuthError(error: unknown): boolean {
  if (isAxiosError(error)) {
    return error.response?.status === 401;
  }
  return false;
}

/**
 * Check if error is a permission/forbidden error (403)
 */
export function isForbiddenError(error: unknown): boolean {
  if (isAxiosError(error)) {
    return error.response?.status === 403;
  }
  return false;
}

/**
 * Check if error is a not found error (404)
 */
export function isNotFoundError(error: unknown): boolean {
  if (isAxiosError(error)) {
    return error.response?.status === 404;
  }
  return false;
}

/**
 * Check if error is a server error (5xx)
 */
export function isServerError(error: unknown): boolean {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    return status !== undefined && status >= 500 && status < 600;
  }
  return false;
}

/**
 * Get HTTP status code from error if available
 */
export function getErrorStatusCode(error: unknown): number | null {
  if (isAxiosError(error)) {
    return error.response?.status ?? null;
  }
  return null;
}
